import { Component, input, OnInit, signal, viewChild } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { WordToKlankConverter } from '../../utils/klank.utils';
import { RidReadingCountdown } from '../rid-reading-countdown/rid-reading-countdown';

@Component({
  selector: 'rid-sentence',
  imports: [RidReadingCountdown],
  templateUrl: './rid-sentence.html',
  host: {
    class: 'block relative',
  },
})
export class RidSentence implements OnInit {
  sentence = input.required<string>();

  countdownComp = viewChild.required<RidReadingCountdown>('countdown');
  text = signal<{ text: string; opacity: number; scale: number; duration: number }[]>([]);
  state = signal<'BLANK' | 'COUNTDOWN' | 'PASS' | 'ASSESS' | 'REVEAL'>('BLANK');

  ngOnInit(): void {
    const words = this.sentence().split(' ');
    this.text.set(
      words.flatMap((word) => {
        const sounds = WordToKlankConverter.breakupWord(word);
        return sounds.map((sound, index) => {
          const isLastSound = index === sounds.length - 1;
          return {
            text: sound + (isLastSound ? ' ' : ''),
            opacity: 0,
            duration: 0,
            scale: 1,
          };
        });
      }),
    );
  }

  show() {
    this.state.set('REVEAL');
    this.text.update((current) => {
      return current.map(({ text }) => ({ text, opacity: 1, scale: 1, duration: 200 }));
    });
  }

  hide() {
    this.state.set('BLANK');
  }

  async countdown(pace: number) {
    this.state.set('COUNTDOWN');
    await delay(500);

    await this.countdownComp().countdown(5 * pace);
  }

  async pass(
    pace: number,
    initial: { opacity: number; scale: number },
    pass: { opacity: number; scale: number },
    mode: 'KLANK' | 'WORD' = 'KLANK',
  ) {
    this.changeAppearance(0, { opacity: 0, scale: initial.scale });

    await this.countdown(pace);

    this.state.set('PASS');
    await this.changeAppearance(250, initial);
    await delay(250);

    if (mode === 'KLANK') {
      await this.showByKlank(pace, pass);
    } else {
      await this.showByWord(pace, pass);
    }

    await delay(2 * pace);

    this.changeAppearance(250, { opacity: 0 });
    this.assess();
  }

  private async changeAppearance(duration: number, properties: Partial<{ opacity: number; scale: number }>) {
    this.text.update((current) => {
      return current.map((sentence) => ({ ...sentence, ...properties, duration: duration }));
    });

    await delay(duration ?? 0);
  }

  public assess() {
    this.state.set('ASSESS');
  }

  private async showByKlank(pace: number, pass: { opacity: number; scale: number }) {
    // Show each klank one at a time
    for (let i = 0; i < this.text().length; i++) {
      this.text.update((current) => {
        const newText = [...current];
        newText[i] = { text: current[i].text, opacity: pass.opacity, scale: pass.scale, duration: pace };
        return newText;
      });
      await delay(pace);
    }
  }

  private async showByWord(pace: number, pass: { opacity: number; scale: number }) {
    // Show each word at a time, with duration based on number of klanks
    let i = 0;
    while (i < this.text().length) {
      const { wordEndIndex, klankCount } = this.findNextWord(i);
      const wordDuration = klankCount * pace;

      this.text.update((current) => {
        const newText = [...current];
        for (let j = i; j < wordEndIndex; j++) {
          newText[j] = { text: current[j].text, opacity: pass.opacity, scale: pass.scale, duration: wordDuration };
        }
        return newText;
      });

      await delay(wordDuration);
      i = wordEndIndex;
    }
  }

  private findNextWord(startIndex: number): { wordEndIndex: number; klankCount: number } {
    let klankCount = 0;

    for (let i = startIndex; i < this.text().length; i++) {
      klankCount++;
      const endsWithSpace = this.text()[i].text.includes(' ');

      if (endsWithSpace) {
        return { wordEndIndex: i + 1, klankCount };
      }
    }

    return { wordEndIndex: this.text().length, klankCount };
  }
}
